import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';

import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Circular, CircularCreate, CircularFilter, CircularUpdate } from '@core/types/circular.type';

@Injectable({ providedIn: 'root' })
export class CircularService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/circulars`;

  grid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<Circular>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    const filter = (config.filter || {}) as CircularFilter;
    if (filter.priority !== undefined) params = params.set('filter.priority', String(filter.priority));
    if (filter.requiresAcknowledgment !== undefined)
      params = params.set('filter.requiresAcknowledgment', String(filter.requiresAcknowledgment));
    if (filter.onlyActive !== undefined) params = params.set('filter.onlyActive', String(filter.onlyActive));
    if (filter.fromDate) params = params.set('filter.fromDate', filter.fromDate);
    if (filter.toDate) params = params.set('filter.toDate', filter.toDate);

    return this.http.get<ApiResponse<PaginationResult<Circular>>>(`${this.baseUrl}/grid`, { params }).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading circulars')),
    );
  }

  get(id: string): Observable<ApiResponse<Circular>> {
    return this.http.get<ApiResponse<Circular>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading circular')),
    );
  }

  create(data: CircularCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error creating circular')),
    );
  }

  update(data: CircularUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error updating circular')),
    );
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error deleting circular')),
    );
  }
}

